import { computed, toValue } from 'vue';
import { storeToRefs } from 'pinia';
import { useMultiATBStore, type BagApiResponse } from '~/stores/useMultiATBStore';
import { useCollectionSelectionStore, type SelectedItem } from '~/stores/useCollectionSelectionStore';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { ExpressCheckoutService } from '~/modules/add-to-bag/services/ExpressCheckout.service';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useLoyalty } from '~/composables/loyalty/useLoyalty';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { tagSTCAddToBag } from '~/features/analytics/shopTheCollection';

export const useSummaryBarActions = (onSuccessCallback: () => void) => {
  const { isMarketPlaceEligibleFlag } = useProductFlags();
  const { isUserNonLoyalist } = useLoyalty();

  const multiATBStore = useMultiATBStore();
  const selectionStore = useCollectionSelectionStore();
  const { brandName } = useCommonProductGetters();

  const {
    killswitches: { isStartCheckoutPostCallEnabled },
  } = useKillswitches();
  const expressCheckoutService = new ExpressCheckoutService({ isStartCheckoutPostCallEnabled });

  const { selections, hasSelections } = storeToRefs(selectionStore);

  const selectedItems = computed<SelectedItem[]>(() => {
    return Object.values(selections.value).filter((item: SelectedItem) => item.quantity > 0);
  });

  const getSuccessfulItems = (response: BagApiResponse | undefined, items: SelectedItem[]) => {
    if (!response || !response.lineItemResults) return items;

    const addedUpcs = new Set(response.lineItemResults.filter((line) => line.status === 'ADDED').map((line) => String(line.upcId)));

    // If no lineItemResults are present, fallback to original items (should not happen on success, but safe fallback)
    if (addedUpcs.size === 0 && response.lineItemResults.length === 0) return items;

    return items.filter((item) => addedUpcs.has(String(item.upcId)));
  };

  const onAddToBag = async () => {
    if (!hasSelections.value) return;

    const payload = selectionStore.getAddToBagPayload({ isMarketPlaceEligibleFlag, loyaltyPoints: !isUserNonLoyalist.value });
    const result = await multiATBStore.addToBag(payload, selectedItems.value);

    if (result?.success) {
      const bagData = result?.data as BagApiResponse;
      const successfulItems = getSuccessfulItems(bagData, selectedItems.value);

      const priceTypeIds = successfulItems.map((item) => String(item.priceTypeId || 'ZERO'));
      const promoMessaging = successfulItems.map((item) => item.promoMessaging || 'no promo messaging');
      selectionStore.clearSelection(true);
      tagSTCAddToBag({
        action: 'add to bag',
        bagData,
        brandName: toValue(brandName),
        priceTypeIds,
        promoMessaging,
      });
      onSuccessCallback();
    }
  };

  const onBuyNow = async () => {
    if (!hasSelections.value) return;

    const payload = selectionStore.getAddToBagPayload({ isMarketPlaceEligibleFlag, loyaltyPoints: !isUserNonLoyalist.value });
    await multiATBStore.buyNow(payload, selectedItems.value, expressCheckoutService, (response) => {
      const bagData = response.data as BagApiResponse;
      const successfulItems = getSuccessfulItems(bagData, selectedItems.value);

      const priceTypeIds = successfulItems.map((item) => String(item.priceTypeId || 'ZERO'));
      const promoMessaging = successfulItems.map((item) => item.promoMessaging || 'no promo messaging');
      tagSTCAddToBag({
        action: 'buy now',
        bagData,
        brandName: toValue(brandName),
        priceTypeIds,
        promoMessaging,
      });

      if (bagData?.status === 'PARTIAL_SUCCESS') {
        selectionStore.clearSelection(true);
        onSuccessCallback();
      }
    });
  };

  return {
    onAddToBag,
    onBuyNow,
  };
};
